import {
  Component, OnInit, OnDestroy, AfterViewInit,
  ElementRef, ViewChild, ViewChildren, QueryList, NgZone,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MfaService } from '../../../core/services/mfa.service';
import { AuthService } from '../../../core/services/auth.service';
import { ThemeService } from '../../../core/services/theme.service';

@Component({
  selector: 'app-mfa-verify',
  templateUrl: './mfa-verify.component.html',
  styleUrls: ['./mfa-verify.component.scss'],
})
export class MfaVerifyComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('canvasWrapper') canvasWrapper!: ElementRef<HTMLDivElement>;
  @ViewChild('cardEl')        cardEl!: ElementRef<HTMLDivElement>;
  @ViewChild('flashEl')       flashEl!: ElementRef<HTMLDivElement>;
  @ViewChildren('otpInput')   otpInputs!: QueryList<ElementRef<HTMLInputElement>>;

  recoveryForm!: FormGroup;
  loading      = false;
  showRecovery = false;
  mfaToken     = '';
  digits: string[] = ['', '', '', '', '', ''];

  private THREE: any;
  private renderer: any;
  private scene: any;
  private camera: any;
  private terrain: any;
  private terrainGeo: any;
  private terrainBase: Float32Array = new Float32Array();
  private terrainMat: any;
  private ico: any;
  private icoMat: any;
  private icoDotsMat: any;
  private icoCoreMat: any;
  private particles: any;
  private partMat: any;
  private partVel: Float32Array = new Float32Array();

  private _focusTs: number[]  = [0, 0, 0, 0, 0, 0];
  private _keydownHandled     = false;
  private raf                 = 0;
  private lastT   = 0;
  private energy  = 0;
  private mouse   = { x: 0, y: 0 };
  private ripples: { x: number; z: number; t0: number }[] = [];
  private cam     = { x: 0, y: 6.5, z: 26, fov: 60, lookY: 3.8 };
  private alive   = false;
  private warpT0: number | null = null;

  private themeSub    = new Subscription();
  private currentTheme = 'dark';

  private static readonly SCENE_THEMES: Record<string, any> = {
    dark:  { fog: 0x07080c, coreFill: 0x07080c, opacity: 1,    blending: 'additive' },
    light: { fog: 0xf4f1ea, coreFill: 0xf4f1ea, opacity: 0.85, blending: 'normal'   },
  };

  constructor(
    private fb: FormBuilder,
    private mfaService: MfaService,
    private auth: AuthService,
    private router: Router,
    private snackBar: MatSnackBar,
    private ngZone: NgZone,
    public themeService: ThemeService,
  ) {}

  ngOnInit(): void {
    this.mfaToken = history.state?.mfaToken || '';
    if (!this.mfaToken) {
      this.router.navigate(['/login']);
      return;
    }
    this.recoveryForm = this.fb.group({ code: ['', Validators.required] });
  }

  ngAfterViewInit(): void {
    this.alive = true;
    this.themeSub = this.themeService.theme$.subscribe(t => {
      this.currentTheme = t;
      this.applySceneTheme();
    });
    this.waitForThree();
    setTimeout(() => this.focusFirst(), 120);
  }

  ngOnDestroy(): void {
    this.alive = false;
    cancelAnimationFrame(this.raf);
    this.themeSub.unsubscribe();
    window.removeEventListener('resize', this.onResize);
    if (this.renderer) {
      this.renderer.dispose();
      const el = this.renderer.domElement as HTMLCanvasElement;
      el.parentNode?.removeChild(el);
    }
  }

  // ── OTP handlers ──────────────────────────────────────────

  private focusFirst(): void {
    const inputs = this.otpInputs?.toArray();
    if (inputs?.length) {
      inputs[0].nativeElement.focus();
      this._focusTs[0] = 0; // Initial focus: don't ghost-block
    }
  }

  private advance(i: number): void {
    if (i >= 5) return;
    setTimeout(() => {
      const next = this.otpInputs.toArray()[i + 1].nativeElement;
      next.value = '';
      this._focusTs[i + 1] = Date.now(); // stamp BEFORE focus so ghost input is blocked even if it fires before the focus event
      next.focus();
    }, 0);
  }

  onFocus(_e: Event, i: number): void {
    this._focusTs[i] = Date.now();
  }

  onDigit(e: Event, i: number): void {
    const input = e.target as HTMLInputElement;

    // Desktop/iOS: keydown already handled this keystroke — just restore displayed value
    if (this._keydownHandled) {
      input.value = this.digits[i] || '';
      return;
    }

    // Mobile ghost-input block: input fired within 30ms of this box receiving focus
    if (Date.now() - this._focusTs[i] < 30) {
      input.value = '';
      return;
    }

    const val = input.value.replace(/\D/g, '');

    // SMS autofill / paste-like: distribute digits across boxes
    if (val.length > 1) {
      const inputs = this.otpInputs.toArray();
      val.slice(0, 6 - i).split('').forEach((ch, j) => {
        inputs[i + j].nativeElement.value = ch;
        this.digits[i + j] = ch;
      });
      inputs[Math.min(i + val.length, 5)].nativeElement.focus();
      if (this.digits.join('').length === 6) setTimeout(() => this.onSubmit(), 60);
      return;
    }

    input.value = val ? val[0] : '';
    this.digits[i] = input.value;
    this.energy = Math.min(3, this.energy + 0.4);
    if (this.digits[i]) this.advance(i);
    if (this.digits.join('').length === 6) setTimeout(() => this.onSubmit(), 60);
  }

  onKey(e: KeyboardEvent, i: number): void {
    // Desktop: intercept digit — preventDefault stops browser from also firing `input`
    if (/^\d$/.test(e.key)) {
      e.preventDefault();
      this._keydownHandled = true;
      const input = this.otpInputs.toArray()[i].nativeElement;
      input.value = e.key;
      this.digits[i] = e.key;
      this.energy = Math.min(3, this.energy + 0.4);
      this.advance(i);
      if (this.digits.join('').length === 6) setTimeout(() => this.onSubmit(), 60);
      // Reset flag after current event cycle (after any residual `input` event fires)
      setTimeout(() => { this._keydownHandled = false; }, 0);
      return;
    }
    if (e.key === 'Backspace') {
      e.preventDefault();
      const input = this.otpInputs.toArray()[i].nativeElement;
      if (this.digits[i]) {
        input.value = '';
        this.digits[i] = '';
      } else if (i > 0) {
        this.digits[i - 1] = '';
        const prev = this.otpInputs.toArray()[i - 1].nativeElement;
        prev.value = '';
        prev.focus();
      }
      return;
    }
    if (e.key === 'ArrowLeft'  && i > 0) this.otpInputs.toArray()[i - 1].nativeElement.focus();
    if (e.key === 'ArrowRight' && i < 5) this.otpInputs.toArray()[i + 1].nativeElement.focus();
  }

  onPaste(e: ClipboardEvent): void {
    e.preventDefault();
    const text = e.clipboardData?.getData('text')?.replace(/\D/g, '').slice(0, 6) || '';
    if (!text) return;
    const inputs = this.otpInputs.toArray();
    text.split('').forEach((ch, i) => {
      if (inputs[i]) {
        inputs[i].nativeElement.value = ch;
        this.digits[i] = ch;
      }
    });
    const focus = Math.min(text.length, 5);
    inputs[focus].nativeElement.focus();
    if (text.length === 6) setTimeout(() => this.onSubmit(), 60);
  }

  // ── Submit handlers ───────────────────────────────────────

  onSubmit(): void {
    const token = this.digits.join('');
    if (token.length < 6 || this.loading) return;
    this.loading = true;

    this.mfaService.verify(token, this.mfaToken).subscribe({
      next: (res: any) => {
        this.auth.completeLogin(res.data.accessToken, res.data.user);
        this.loading = false;
        this.triggerWarp(() => this.router.navigate([this.auth.getHomeRoute()]));
      },
      error: (err: any) => {
        this.loading = false;
        this.digits = ['', '', '', '', '', ''];
        this.otpInputs.toArray().forEach(el => (el.nativeElement.value = ''));
        this.focusFirst();
        this.snackBar.open(err?.error?.message || 'Código inválido. Tente novamente.', 'Fechar', { duration: 4000 });
      },
    });
  }

  onRecovery(): void {
    if (this.recoveryForm.invalid) return;
    this.loading = true;

    this.mfaService.recovery(this.recoveryForm.value.code, this.mfaToken).subscribe({
      next: (res: any) => {
        this.auth.completeLogin(res.data.accessToken, res.data.user);
        this.loading = false;
        this.triggerWarp(() => this.router.navigate([this.auth.getHomeRoute()]));
      },
      error: (err: any) => {
        this.loading = false;
        this.snackBar.open(err?.error?.message || 'Código de recuperação inválido.', 'Fechar', { duration: 4000 });
      },
    });
  }

  toggleRecovery(): void {
    this.showRecovery = !this.showRecovery;
    if (!this.showRecovery) {
      this.digits = ['', '', '', '', '', ''];
      setTimeout(() => {
        this.otpInputs?.toArray().forEach(el => (el.nativeElement.value = ''));
        this.focusFirst();
      }, 60);
    }
  }

  private triggerWarp(onDone: () => void): void {
    const card = this.cardEl?.nativeElement;
    if (card) {
      card.style.transition = 'transform 0.9s cubic-bezier(0.5,0,1,1), opacity 0.8s ease';
      card.style.transform  = 'translateZ(380px) rotateX(6deg)';
      card.style.opacity    = '0';
    }
    setTimeout(() => {
      if (!this.alive) return;
      this.warpT0 = performance.now();
      setTimeout(onDone, 1450);
    }, 400);
  }

  // ── Mouse ─────────────────────────────────────────────────

  onMouseMove(e: MouseEvent): void {
    const nx = (e.clientX / window.innerWidth) * 2 - 1;
    const ny = (e.clientY / window.innerHeight) * 2 - 1;
    this.mouse = { x: nx, y: ny };
    if (this.cardEl?.nativeElement && !this.warpT0) {
      this.cardEl.nativeElement.style.transform =
        `rotateY(${(nx * 6).toFixed(2)}deg) rotateX(${(-ny * 5).toFixed(2)}deg)`;
    }
  }

  // ── Scene bootstrap ───────────────────────────────────────

  private waitForThree(): void {
    if (!this.alive) return;
    const T = (window as any).THREE;
    if (T && this.canvasWrapper?.nativeElement) {
      this.THREE = T;
      this.ngZone.runOutsideAngular(() => this.initScene());
    } else {
      setTimeout(() => this.waitForThree(), 60);
    }
  }

  private initScene(): void {
    const THREE  = this.THREE;
    const holder = this.canvasWrapper.nativeElement;
    try { this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true }); }
    catch { return; }

    const w = holder.clientWidth  || window.innerWidth;
    const h = holder.clientHeight || window.innerHeight;
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.setSize(w, h);
    holder.appendChild(this.renderer.domElement);

    this.scene  = new THREE.Scene();
    this.scene.fog = new THREE.FogExp2(0x07080c, 0.026);
    this.camera = new THREE.PerspectiveCamera(60, w / h, 0.1, 220);
    this.camera.position.set(0, 6.5, 26);

    const accent = new THREE.Color('#F79520');

    const geo = new THREE.PlaneGeometry(170, 100, 112, 62);
    geo.rotateX(-Math.PI / 2);
    this.terrainGeo  = geo;
    this.terrainBase = new Float32Array(geo.attributes.position.array);
    this.terrainMat  = new THREE.PointsMaterial({ color: accent, size: 0.16, transparent: true, opacity: 0.75, sizeAttenuation: true });
    this.terrain     = new THREE.Points(geo, this.terrainMat);
    this.terrain.position.y = -4.5;
    this.scene.add(this.terrain);

    const icoGeo     = new THREE.IcosahedronGeometry(3.6, 1);
    this.icoMat      = new THREE.MeshBasicMaterial({ color: accent, wireframe: true, transparent: true, opacity: 0.5 });
    this.ico         = new THREE.Mesh(icoGeo, this.icoMat);
    this.ico.position.set(0, 6, -9);
    this.scene.add(this.ico);
    this.icoCoreMat  = new THREE.MeshBasicMaterial({ color: 0x07080c });
    this.ico.add(new THREE.Mesh(new THREE.IcosahedronGeometry(3.45, 1), this.icoCoreMat));
    this.icoDotsMat  = new THREE.PointsMaterial({ color: accent, size: 0.18, transparent: true, opacity: 0.9 });
    this.ico.add(new THREE.Points(icoGeo, this.icoDotsMat));

    this.rebuildParticles();
    this.applySceneTheme();

    window.addEventListener('resize', this.onResize);
    this.raf = requestAnimationFrame(this.animate);
  }

  private rebuildParticles(): void {
    if (!this.THREE || !this.scene) return;
    const THREE = this.THREE;
    if (this.particles) { this.scene.remove(this.particles); this.particles.geometry.dispose(); }
    const count = 500;
    const pos   = new Float32Array(count * 3);
    this.partVel = new Float32Array(count);
    for (let i = 0; i < count; i++) {
      pos[i * 3]     = (Math.random() - 0.5) * 130;
      pos[i * 3 + 1] = Math.random() * 34 - 4;
      pos[i * 3 + 2] = (Math.random() - 0.5) * 90 - 10;
      this.partVel[i] = 0.3 + Math.random() * 1.1;
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    this.partMat  = new THREE.PointsMaterial({ color: new THREE.Color('#F79520'), size: 0.22, transparent: true, opacity: 0.55, blending: THREE.AdditiveBlending, depthWrite: false });
    this.particles = new THREE.Points(g, this.partMat);
    this.scene.add(this.particles);
  }

  private applySceneTheme(): void {
    if (!this.scene || !this.THREE) return;
    const T = MfaVerifyComponent.SCENE_THEMES[this.currentTheme];
    const c = new this.THREE.Color('#F79520');
    this.scene.fog.color.setHex(T.fog);
    this.icoCoreMat.color.setHex(T.coreFill);
    this.terrainMat.color.copy(c);
    this.terrainMat.opacity = 0.75 * T.opacity;
    this.icoMat.color.copy(c);
    this.icoDotsMat.color.copy(c);
    this.partMat.color.copy(c);
    this.partMat.blending  = T.blending === 'additive' ? this.THREE.AdditiveBlending : this.THREE.NormalBlending;
    this.partMat.opacity   = T.blending === 'additive' ? 0.55 : 0.4;
    this.partMat.needsUpdate = true;
  }

  private onResize = (): void => {
    if (!this.renderer || !this.canvasWrapper?.nativeElement) return;
    const { clientWidth: w, clientHeight: h } = this.canvasWrapper.nativeElement;
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(w, h);
  };

  private animate = (): void => {
    if (!this.alive) return;
    this.raf = requestAnimationFrame(this.animate);
    const now = performance.now();
    const t   = now / 1000;
    const dt  = Math.min(0.05, t - (this.lastT || t));
    this.lastT = t;

    this.energy *= 0.94;
    this.ripples = this.ripples.filter(r => t - r.t0 < 3);

    const pos  = this.terrainGeo.attributes.position;
    const base = this.terrainBase;
    for (let i = 0; i < pos.count; i++) {
      const x = base[i * 3], z = base[i * 3 + 2];
      let y = Math.sin(x * 0.14 + t * 0.7) * 1.1
            + Math.cos(z * 0.18 + t * 0.5) * 0.9
            + Math.sin((x + z) * 0.07 + t * 0.32) * 0.6;
      for (const r of this.ripples) {
        const age = t - r.t0, dx = x - r.x, dz = z - r.z;
        const d   = Math.sqrt(dx * dx + dz * dz);
        y += Math.sin(d * 1.3 - age * 10) * 1.8 * Math.exp(-d * 0.16 - age * 1.6);
      }
      pos.setY(i, y);
    }
    pos.needsUpdate = true;

    this.ico.rotation.y += dt * (0.25 + this.energy * 1.4);
    this.ico.rotation.x += dt * (0.08 + this.energy * 0.5);
    const s = 1 + this.energy * 0.06;
    this.ico.scale.set(s, s, s);
    this.ico.position.y = 6 + Math.sin(t * 0.6) * 0.5;

    const pp = this.particles.geometry.attributes.position;
    for (let i = 0; i < pp.count; i++) {
      let y = pp.getY(i) + this.partVel[i] * dt * (1 + this.energy);
      if (y > 32) y = -4;
      pp.setY(i, y);
    }
    pp.needsUpdate = true;

    if (this.warpT0 !== null) {
      const k = Math.min(1, (now - this.warpT0) / 1400);
      const e = k * k * k;
      this.camera.position.set(this.cam.x, this.cam.y, this.cam.z - e * (this.cam.z + 12));
      this.camera.fov = this.cam.fov + e * 42;
      this.camera.updateProjectionMatrix();
      if (this.flashEl?.nativeElement) {
        this.flashEl.nativeElement.style.transition = 'none';
        this.flashEl.nativeElement.style.opacity    = String(Math.max(0, (k - 0.5) / 0.5));
      }
    } else {
      this.cam.x += (this.mouse.x * 3.2 - this.cam.x) * 0.045;
      this.cam.y += (6.5 - this.mouse.y * 1.8 - this.cam.y) * 0.045;
      this.camera.position.set(this.cam.x, this.cam.y, this.cam.z);
      this.camera.fov = this.cam.fov;
      this.camera.updateProjectionMatrix();
    }

    this.camera.lookAt(0, this.cam.lookY, -4);
    this.renderer.render(this.scene, this.camera);
  };
}
